// OAuth 2.0 authorization-code flow against Cognito Hosted UI.
//
// Three top-level operations:
//
//   redirectToHostedUi(config, opts?) — start a login. Generates PKCE
//     verifier/challenge + state, stashes them in sessionStorage (along
//     with the original location so we can return there post-callback),
//     and navigates the browser to the Cognito /oauth2/authorize URL.
//
//   handleCallback(config) — finish a login. Reads ?code=&state= from the
//     current URL, validates state, posts to /oauth2/token, and returns
//     the tokens + the original returnTo path. Caller is responsible for
//     stashing the tokens via its storage adapter.
//
//   refreshAccessToken(config, refreshToken) — exchange a long-lived
//     refresh token for a fresh access token (PMA only).
//
//   redirectToLogout(config) — end the SSO session. Navigates to Cognito
//     /logout which drops the SSO cookie and redirects back to
//     `logoutRedirectUri`.
//
// Cognito-side endpoints (relative to the Hosted UI domain, e.g.
// auth-test.goodstewardaba.com):
//   GET  /oauth2/authorize  — login + consent
//   POST /oauth2/token      — code↔token + refresh exchange
//   GET  /logout            — drops SSO cookie, redirects
//
// Reference: https://docs.aws.amazon.com/cognito/latest/developerguide/cognito-userpools-server-contract-reference.html

import { challengeFromVerifier, randomState, randomVerifier } from './pkce.js';

const SS_KEY_VERIFIER = 'gs.oauth.verifier';
const SS_KEY_STATE    = 'gs.oauth.state';
const SS_KEY_RETURN   = 'gs.oauth.returnTo';

function ss() {
    if (typeof globalThis.sessionStorage === 'undefined') {
        throw new Error('sessionStorage is not available');
    }
    return globalThis.sessionStorage;
}

function loc() {
    if (typeof globalThis.location === 'undefined') {
        throw new Error('window.location is not available');
    }
    return globalThis.location;
}

/**
 * Build the /oauth2/authorize URL for a given config + PKCE challenge.
 * Exported separately so tests can assert query-string shape without
 * triggering an actual navigation.
 */
/**
 * Build the absolute origin (scheme + host + port) the OAuth endpoints
 * sit at. Accepts:
 *   - a bare hostname  ("auth-test.example.com")  → assumes https
 *   - a full origin URL ("http://localhost:9000") → used as-is
 *
 * The full-origin form is what the local stub IdP needs, since dev runs
 * over plain http on localhost. Test/prod always pass bare hostnames so
 * the behavior there is unchanged.
 */
function buildOrigin(config) {
    const d = String(config.domain || '');
    if (/^https?:\/\//i.test(d)) return d.replace(/\/$/, '');
    return `https://${d}`;
}

export function buildAuthorizeUrl(config, { state, codeChallenge, prompt }) {
    const base = `${buildOrigin(config)}/oauth2/authorize`;
    const params = new URLSearchParams({
        client_id:             config.clientId,
        response_type:         'code',
        scope:                 (config.scopes || ['openid', 'email', 'profile']).join(' '),
        redirect_uri:          config.redirectUri,
        state,
        code_challenge:        codeChallenge,
        code_challenge_method: 'S256',
    });
    if (prompt) params.set('prompt', prompt);
    return `${base}?${params.toString()}`;
}

/**
 * Generate PKCE + state, stash them in sessionStorage, and navigate to the
 * Cognito authorize URL. Caller never returns from this function — the
 * browser unloads the page on assignment to location.assign().
 *
 * Pass { prompt: 'none' } to attempt a silent re-auth via the SSO cookie.
 * If the SSO cookie is gone, Cognito redirects back to redirectUri with
 * `error=login_required` instead of a code; handleCallback() surfaces
 * that as a thrown OAuthError so the caller can fall back to a real login.
 */
export async function redirectToHostedUi(config, opts = {}) {
    const verifier  = randomVerifier();
    const state     = randomState();
    const challenge = await challengeFromVerifier(verifier);

    const store = ss();
    store.setItem(SS_KEY_VERIFIER, verifier);
    store.setItem(SS_KEY_STATE, state);
    if (opts.returnTo !== undefined) {
        store.setItem(SS_KEY_RETURN, opts.returnTo);
    } else {
        // Default: come back to wherever the user was. Hash + search are
        // preserved; Cognito callback wins the URL slot during the round-
        // trip but we restore on completion. Auth-flow paths (/login,
        // /auth/*) are excluded — stashing /login here means a successful
        // round trip lands the now-authenticated user back on /login,
        // which doesn't redirect away (no auth check in the route) and
        // looks like the login simply didn't take.
        const { pathname, search, hash } = loc();
        const isAuthFlowPath = pathname === '/login' || pathname.startsWith('/auth/');
        store.setItem(SS_KEY_RETURN, isAuthFlowPath ? '/' : `${pathname}${search}${hash}`);
    }

    const url = buildAuthorizeUrl(config, {
        state,
        codeChallenge: challenge,
        prompt: opts.prompt,
    });
    loc().assign(url);
}

export class OAuthError extends Error {
    constructor(code, description) {
        super(description ? `${code}: ${description}` : code);
        this.name = 'OAuthError';
        this.code = code;
        this.description = description || null;
    }
}

/**
 * Read the callback URL params, validate state, exchange code for tokens.
 * Throws OAuthError on `error=...` callbacks (including login_required from
 * a prompt=none silent-reauth attempt).
 *
 * Returns { accessToken, idToken, refreshToken, expiresAt, returnTo }.
 */
export async function handleCallback(config, { url } = {}) {
    const target = url ? new URL(url) : new URL(loc().href);
    const params = target.searchParams;

    const err = params.get('error');
    if (err) {
        throw new OAuthError(err, params.get('error_description'));
    }

    const code     = params.get('code');
    const stateIn  = params.get('state');
    if (!code || !stateIn) {
        throw new OAuthError('invalid_callback', 'missing code or state');
    }

    const store = ss();
    const stateExpected = store.getItem(SS_KEY_STATE);
    const verifier      = store.getItem(SS_KEY_VERIFIER);
    const returnTo      = store.getItem(SS_KEY_RETURN) || '/';
    // Wipe the one-shot values immediately — the verifier in particular
    // must not survive a successful exchange.
    store.removeItem(SS_KEY_VERIFIER);
    store.removeItem(SS_KEY_STATE);
    store.removeItem(SS_KEY_RETURN);

    if (!stateExpected || stateIn !== stateExpected) {
        throw new OAuthError('state_mismatch', 'state did not match the stashed value');
    }
    if (!verifier) {
        throw new OAuthError('missing_verifier', 'PKCE verifier missing from sessionStorage');
    }

    const tokens = await postToTokenEndpoint(config, {
        grant_type:    'authorization_code',
        code,
        redirect_uri:  config.redirectUri,
        client_id:     config.clientId,
        code_verifier: verifier,
    });

    return {
        accessToken:  tokens.access_token,
        idToken:      tokens.id_token,
        refreshToken: tokens.refresh_token || null,
        expiresAt:    Date.now() + (tokens.expires_in || 0) * 1000,
        returnTo,
    };
}

export async function refreshAccessToken(config, refreshToken) {
    if (!refreshToken) throw new OAuthError('missing_refresh_token', 'no refresh token to exchange');
    const tokens = await postToTokenEndpoint(config, {
        grant_type:    'refresh_token',
        refresh_token: refreshToken,
        client_id:     config.clientId,
    });
    return {
        accessToken:  tokens.access_token,
        idToken:      tokens.id_token,
        // Cognito does not rotate refresh tokens by default — re-use the
        // existing one if the response omits it.
        refreshToken: tokens.refresh_token || refreshToken,
        expiresAt:    Date.now() + (tokens.expires_in || 0) * 1000,
    };
}

export function redirectToLogout(config) {
    const url = new URL(`${buildOrigin(config)}/logout`);
    url.searchParams.set('client_id', config.clientId);
    url.searchParams.set('logout_uri', config.logoutRedirectUri);
    loc().assign(url.toString());
}

async function postToTokenEndpoint(config, body) {
    const url = `${buildOrigin(config)}/oauth2/token`;
    const form = new URLSearchParams(body);
    const fetcher = config.fetch || globalThis.fetch;
    const res = await fetcher(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: form.toString(),
    });
    if (!res.ok) {
        let detail = '';
        try {
            const j = await res.json();
            detail = j.error_description || j.error || '';
        } catch { /* not JSON */ }
        throw new OAuthError('token_endpoint_error', `HTTP ${res.status}${detail ? ': ' + detail : ''}`);
    }
    return res.json();
}
